import React from 'react';
import { buildInstallationAttention, type InstallationAttentionState } from '../../routes/dashboardAttentionLogic';

export interface InstallationAttentionModuleProps {
  /** Only accounts that can manage installations may read the summary. */
  enabled: boolean;
}

/**
 * "What needs attention" for the Portal dashboard. Reads the compact
 * installation summary (heartbeat, maintenance, attention rows) and states
 * plainly whether anything needs a look — including when it cannot tell.
 */
const InstallationAttentionModule = ({ enabled }: InstallationAttentionModuleProps): JSX.Element | null => {
  const [state, setState] = React.useState<InstallationAttentionState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (!enabled) return;
    let disposed = false;
    setState({ kind: 'loading' });
    const run = async (): Promise<void> => {
      try {
        const response = await fetch('/api/instances', { credentials: 'same-origin' });
        const payload = response.ok ? await response.json().catch(() => undefined) : undefined;
        if (!disposed) setState(buildInstallationAttention(payload, response.status));
      } catch {
        if (!disposed) setState({ kind: 'unavailable' });
      }
    };
    void run();
    return () => { disposed = true; };
  }, [enabled, reloadToken]);

  if (!enabled) return null;

  const retry = (
    <button type="button" className="btn secondary" onClick={() => setReloadToken(value => value + 1)}>
      Check again
    </button>
  );

  if (state.kind === 'loading') {
    return (
      <div className="dashboard-attention is-loading" role="status" aria-live="polite">
        <span className="dashboard-attention__headline">Checking installations…</span>
      </div>
    );
  }
  if (state.kind === 'forbidden') {
    return null;
  }
  if (state.kind === 'unavailable') {
    return (
      <div className="dashboard-attention severity-warning" role="status">
        <div className="dashboard-attention__text">
          <span className="dashboard-attention__headline">Installation status is unavailable right now.</span>
          <span className="dashboard-attention__detail">The inventory could not be read, so nothing here is confirmed healthy.</span>
        </div>
        <div className="dashboard-attention__actions">{retry}</div>
      </div>
    );
  }
  if (state.kind === 'none-registered') {
    return (
      <div className="dashboard-attention severity-neutral" role="status">
        <div className="dashboard-attention__text">
          <span className="dashboard-attention__headline">No installations have registered yet.</span>
          <span className="dashboard-attention__detail">An installation appears here once the web part sends its first heartbeat.</span>
        </div>
        <div className="dashboard-attention__actions">
          <a className="btn secondary" href="/instances">Open installations</a>
        </div>
      </div>
    );
  }
  if (state.kind === 'clear') {
    return (
      <div className="dashboard-attention severity-success" role="status">
        <div className="dashboard-attention__text">
          <span className="dashboard-attention__headline">
            {state.activeCount === 1 ? 'Your installation is healthy.' : `All ${state.activeCount} installations are healthy.`}
          </span>
          <span className="dashboard-attention__detail">Heartbeats are current and no maintenance needs a decision.</span>
        </div>
        <div className="dashboard-attention__actions">
          <a className="btn secondary" href="/instances">Open installations</a>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard-attention severity-${state.severity}`} role="status">
      <div className="dashboard-attention__text">
        <span className="dashboard-attention__headline">{state.headline}</span>
        <ul className="dashboard-attention__list">
          {state.items.map(item => (
            <li key={item.key} className={`dashboard-attention__chip severity-${item.severity}`}>{item.label}</li>
          ))}
        </ul>
      </div>
      <div className="dashboard-attention__actions">
        <a className="btn" href="/instances">Review installations</a>
      </div>
    </div>
  );
};

export default InstallationAttentionModule;
